import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Download, Upload, Search, ChevronDown, ChevronUp } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import * as XLSX from 'xlsx';
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { useQuery } from "@tanstack/react-query";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

type Student = {
  id: string;
  surname: string;
  other_names: string | null;
  class: string;
  stream: string | null;
  tutor1_name: string | null;
  tutor1_contact: string | null;
  tutor2_name: string | null;
  tutor2_contact: string | null;
  emergency_contact_name: string | null;
  emergency_contact_phone: string | null;
};

export function StudentBulkEdit() {
  const [isGenerating, setIsGenerating] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStudents, setSelectedStudents] = useState<Set<string>>(new Set());
  const [isCollapsed, setIsCollapsed] = useState(true); // Changed to true for default collapsed state
  const [selectedClass, setSelectedClass] = useState<string>("All...");
  const [selectedStream, setSelectedStream] = useState<string>("All...");
  const { toast } = useToast();
  const { user } = useAuth();

  const { data: students = [], isLoading, refetch } = useQuery({
    queryKey: ['students', user?.schoolId],
    queryFn: async () => {
      if (!user?.schoolId) {
        console.error("No school ID found for user");
        return [];
      }
      
      const { data: studentsData, error: studentsError } = await supabase
        .from('students')
        .select('id, surname, other_names, class, stream, tutor1_name, tutor1_contact, tutor2_name, tutor2_contact, emergency_contact_name, emergency_contact_phone')
        .eq('school_id', user.schoolId)
        .order('surname');

      if (studentsError) {
        console.error("Error fetching students:", studentsError);
        throw studentsError;
      }

      return studentsData as Student[];
    },
    gcTime: 5 * 60 * 1000, // 5 minutes
  });

  // Get unique classes and streams for filters
  const classes = Array.from(new Set(students.map(student => student.class))).sort();
  const streams = Array.from(new Set(students.map(student => student.stream).filter(Boolean))).sort();

  const filteredStudents = students.filter(student => {
    const matchesSearch = 
      student.surname.toLowerCase().includes(searchQuery.toLowerCase()) ||
      student.other_names?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      student.class.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesClass = selectedClass === "All..." || student.class === selectedClass;
    const matchesStream = selectedStream === "All..." || student.stream === selectedStream;

    return matchesSearch && matchesClass && matchesStream;
  });

  const toggleStudent = (studentId: string) => {
    const newSelected = new Set(selectedStudents);
    if (newSelected.has(studentId)) {
      newSelected.delete(studentId);
    } else {
      newSelected.add(studentId);
    }
    setSelectedStudents(newSelected);
  };

  const generateTemplate = async () => {
    if (!user?.schoolId) {
      toast({
        title: "Error",
        description: "No school ID found",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsGenerating(true);
      toast({
        title: "Generating template",
        description: "Please wait while we generate your template...",
      });
      
      const selectedStudentsData = (selectedStudents.size > 0 ? 
        students.filter(student => selectedStudents.has(student.id)) : 
        students).map(student => ({
          ID: student.id,
          Surname: student.surname,
          'Other Names': student.other_names || '',
          Class: student.class,
          Stream: student.stream || '',
          'Tutor 1 Name': student.tutor1_name || '',
          'Tutor 1 Contact': student.tutor1_contact || '',
          'Tutor 2 Name': student.tutor2_name || '',
          'Tutor 2 Contact': student.tutor2_contact || '',
          'Emergency Contact Name': student.emergency_contact_name || '',
          'Emergency Contact Phone': student.emergency_contact_phone || '',
        }));

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(selectedStudentsData);

      // Set column widths
      ws['!cols'] = [
        { wch: 10 }, // ID
        { wch: 15 }, // Surname
        { wch: 15 }, // Other Names
        { wch: 10 }, // Class
        { wch: 10 }, // Stream
        { wch: 20 }, // Tutor 1 Name
        { wch: 15 }, // Tutor 1 Contact
        { wch: 20 }, // Tutor 2 Name
        { wch: 15 }, // Tutor 2 Contact
        { wch: 20 }, // Emergency Contact Name
        { wch: 15 }, // Emergency Contact Phone
      ];

      XLSX.utils.book_append_sheet(wb, ws, "Students");
      XLSX.writeFile(wb, "student_bulk_edit_template.xlsx");

      toast({
        title: "Success",
        description: `Template generated with ${selectedStudentsData.length} student${selectedStudentsData.length !== 1 ? 's' : ''}`,
      });
    } catch (error) {
      console.error("Error generating template:", error);
      toast({
        title: "Error",
        description: "Failed to generate template",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user?.schoolId) {
      toast({
        title: "Error",
        description: "Please select a file and ensure you're logged in",
        variant: "destructive",
      });
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    let successCount = 0;
    let errorCount = 0;
    let errorMessages: string[] = [];
    
    toast({
      title: "Processing",
      description: "Uploading and processing your file...",
    });

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);
      const totalRows = jsonData.length;

      for (const [index, row] of Object.entries(jsonData as any[])) {
        try {
          if (!row.ID) {
            errorCount++;
            errorMessages.push(`Row missing ID`);
            continue;
          }

          const updateData = {
            surname: row.Surname,
            other_names: row['Other Names'] || null,
            class: row.Class,
            stream: row.Stream || null,
            tutor1_name: row['Tutor 1 Name'] || null,
            tutor1_contact: row['Tutor 1 Contact'] || null,
            tutor2_name: row['Tutor 2 Name'] || null,
            tutor2_contact: row['Tutor 2 Contact'] || null,
            emergency_contact_name: row['Emergency Contact Name'] || null,
            emergency_contact_phone: row['Emergency Contact Phone'] || null,
          };

          const { error: updateError } = await supabase
            .from('students')
            .update(updateData)
            .eq('id', row.ID)
            .eq('school_id', user.schoolId);

          if (updateError) {
            errorCount++;
            errorMessages.push(`Failed to update student ${row.ID}: ${updateError.message}`);
          } else {
            successCount++;
          }

          // Update progress
          const progress = Math.round(((Number(index) + 1) / totalRows) * 100);
          setUploadProgress(progress);

        } catch (error) {
          errorCount++;
          errorMessages.push(`Error processing student ${row.ID}: ${error}`);
        }
      }

      await refetch();

      if (errorCount > 0) {
        toast({
          title: successCount > 0 ? "Partial Success" : "Error",
          description: `Updated ${successCount} student${successCount !== 1 ? 's' : ''}. Failed to update ${errorCount} student${errorCount !== 1 ? 's' : ''}.${errorMessages.length > 0 ? '\nErrors: ' + errorMessages.slice(0, 3).join('\n') : ''}`,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Success",
          description: `Successfully updated ${successCount} student${successCount !== 1 ? 's' : ''}.`,
        });
      }
    } catch (error) {
      console.error("Error uploading student data:", error);
      toast({
        title: "Error",
        description: "Failed to process the file. Please check the format and try again.",
        variant: "destructive",
      });
    } finally {
      setTimeout(() => {
        setIsUploading(false);
        setUploadProgress(0);
        if (e.target) {
          e.target.value = '';
        }
      }, 500);
    }
  };

  return (
    <Card>
      <CardHeader className="cursor-pointer" onClick={() => setIsCollapsed(!isCollapsed)}>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Bulk Student Edit</CardTitle>
            <CardDescription>
              Download a template to edit multiple students' information at once
            </CardDescription>
          </div>
          {isCollapsed ? <ChevronDown className="h-6 w-6" /> : <ChevronUp className="h-6 w-6" />}
        </div>
      </CardHeader>
      <div className="px-6 pb-4 flex flex-col sm:flex-row gap-4">
        <Button
          onClick={generateTemplate}
          disabled={isGenerating}
          variant="default"
          className="w-full sm:w-auto bg-green-600 hover:bg-green-700"
        >
          <Download className="mr-2 h-4 w-4" />
          {isGenerating ? "Generating..." : selectedStudents.size > 0 ? 
            "Download Template (Selected)" : "Download Template (All)"}
        </Button>
        
        <div className="relative w-full sm:w-auto">
          <input
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileUpload}
            disabled={isUploading}
            className="absolute inset-0 cursor-pointer opacity-0"
          />
          <Button
            variant="outline"
            className="w-full sm:w-auto"
            disabled={isUploading}
          >
            <Upload className="mr-2 h-4 w-4" />
            {isUploading ? "Uploading..." : "Upload Template"}
          </Button>
        </div>
      </div>
      {isUploading && (
        <div className="px-6 pb-4">
          <Progress value={uploadProgress} className="w-full" />
          <p className="text-sm text-muted-foreground mt-2">
            Uploading... {uploadProgress}%
          </p>
        </div>
      )}
      {!isCollapsed && (
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search students..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8"
              />
            </div>
            <Select value={selectedClass} onValueChange={setSelectedClass}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Class..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="All...">All Classes</SelectItem>
                {classes.map(className => (
                  <SelectItem key={className} value={className}>
                    {className}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={selectedStream} onValueChange={setSelectedStream}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Stream..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="All...">All Streams</SelectItem>
                {streams.map(streamName => (
                  <SelectItem key={streamName} value={streamName}>
                    {streamName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                Selected: {selectedStudents.size}
              </span>
            </div>
          </div>

          <div className="border rounded-md">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12"></TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Class</TableHead>
                  <TableHead>Stream</TableHead>
                  <TableHead>Emergency Contact</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-4">
                      Loading students...
                    </TableCell>
                  </TableRow>
                ) : filteredStudents.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-4">
                      No students found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredStudents.map((student) => (
                    <TableRow key={student.id}>
                      <TableCell>
                        <Checkbox
                          checked={selectedStudents.has(student.id)}
                          onCheckedChange={() => toggleStudent(student.id)}
                        />
                      </TableCell>
                      <TableCell>
                        {student.surname} {student.other_names}
                      </TableCell>
                      <TableCell>{student.class}</TableCell>
                      <TableCell>{student.stream || '-'}</TableCell>
                      <TableCell>
                        {student.emergency_contact_name || '-'}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          
          <div className="text-sm text-muted-foreground">
            Download the template, update the student information, and upload it back to update student records.
            {selectedStudents.size > 0 && " Only selected students will be included in the template."}
          </div>
        </CardContent>
      )}
    </Card>
  );
}